/**
 * V15 AudioEngine — 音频系统主管道
 *
 * 管理 BGM (MP3) / MIDI / SFX 的播放、切换、音量控制。
 * 实现设计文档 §1.5 的场景过渡模型。
 *
 * 架构：
 *   AudioContext (master) → masterGain → destination
 *     ├── bgmNode (MediaElementAudioSourceNode from HTMLAudioElement)
 *     │     └── bgmGain
 *     ├── midiNode (Web Audio synthesis via midi-synth.ts)
 *     │     └── midiGain
 *     └── sfxNode (Web Audio synthesis via sfx-synth.ts)
 *           └── sfxGain
 */

import { playMidi, type MidiPlaybackHandle } from './midi-synth';
import { playSfx, type SfxId } from './sfx-synth';

export type BgmId = 'armory-hall' | 'battle-baseline' | 'victory' | 'defeat';
export type MidiId =
  | 'armory-hall-simple'
  | 'battle-baseline'
  | 'battle-advantage'
  | 'victory-simple'
  | 'defeat-minimal'
  | 'sting';

export type ScreenId = string; // 我们不知道所有 ScreenId，用 string

const BGM_FILES: Record<BgmId, string> = {
  'armory-hall': '/audio/bgm/bgm-armory-hall.webm',
  'battle-baseline': '/audio/bgm/bgm-battle-baseline.webm',
  'victory': '/audio/bgm/bgm-victory.webm',
  'defeat': '/audio/bgm/bgm-defeat.webm',
};

const MIDI_FILES: Record<MidiId, string> = {
  'armory-hall-simple': '/audio/midi/midi-armory-hall-simple.mid',
  'battle-baseline': '/audio/midi/midi-battle-baseline.mid',
  'battle-advantage': '/audio/midi/midi-battle-advantage.mid',
  'victory-simple': '/audio/midi/midi-victory-simple.mid',
  'defeat-minimal': '/audio/midi/midi-defeat-minimal.mid',
  'sting': '/audio/midi/midi-sting.mid',
};

// ---- Volume levels (0-1) ----
const DEFAULT_MASTER_VOLUME = 0.7;
const DEFAULT_BGM_VOLUME = 0.8;
const DEFAULT_SFX_VOLUME = 0.6;
const DEFAULT_MIDI_VOLUME = 0.5;

class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private midiGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  // BGM state
  private bgmElement: HTMLAudioElement | null = null;
  private bgmSource: MediaElementAudioSourceNode | null = null;
  private currentBgmId: BgmId | null = null;

  // MIDI state
  private currentMidiId: MidiId | null = null;
  private midiPlayback: MidiPlaybackHandle | null = null;
  private midiBuffer: ArrayBuffer | null = null;

  // Volume state (persisted to localStorage)
  private masterVolume = DEFAULT_MASTER_VOLUME;
  private bgmVolume = DEFAULT_BGM_VOLUME;
  private sfxVolume = DEFAULT_SFX_VOLUME;
  private midiVolume = DEFAULT_MIDI_VOLUME;
  private isMuted = false;

  // Initialization state
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.loadVolumeSettings();
  }

  // ---- Lifecycle ----

  /**
   * Initialize AudioContext. Must be called from a user gesture context
   * (click/keydown) to satisfy browser autoplay policy.
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    try {
      this.ctx = new AudioContext();

      // Master chain
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.isMuted ? 0 : this.masterVolume;
      this.masterGain.connect(this.ctx.destination);

      // BGM chain
      this.bgmGain = this.ctx.createGain();
      this.bgmGain.gain.value = this.bgmVolume;
      this.bgmGain.connect(this.masterGain);

      // MIDI chain
      this.midiGain = this.ctx.createGain();
      this.midiGain.gain.value = this.midiVolume;
      this.midiGain.connect(this.masterGain);

      // SFX chain
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.sfxVolume;
      this.sfxGain.connect(this.masterGain);

      // Resume if suspended (autoplay policy)
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }

      this.initialized = true;
      console.info('[audio] AudioEngine initialized, sampleRate:', this.ctx.sampleRate);
    } catch (e) {
      console.warn('[audio] AudioContext init failed:', e);
      this.ctx = null;
      this.initPromise = null;
    }
  }

  /**
   * Ensure AudioContext is running. Call at the start of any playback method.
   */
  private async ensureContext(): Promise<AudioContext | null> {
    if (!this.initialized) {
      await this.init();
    }
    if (!this.ctx) return null;

    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        return null;
      }
    }
    return this.ctx;
  }

  // ---- BGM (MP3) ----

  /**
   * Play a BGM track. Automatically stops current BGM.
   */
  async playBgm(
    id: BgmId,
    options: {
      loop?: boolean;
      fadeIn?: number;      // ms
      volume?: number;       // 0-1 override
    } = {}
  ): Promise<void> {
    const { loop = true, fadeIn = 0, volume } = options;
    const ctx = await this.ensureContext();
    if (!ctx) return;

    // Stop current BGM
    if (this.currentBgmId || this.bgmElement) {
      await this.stopBgm(100); // quick fade
    }

    const src = BGM_FILES[id];

    const audio = new Audio(src);
    audio.loop = loop;
    audio.preload = 'metadata';

    // Create MediaElement source
    this.bgmSource = ctx.createMediaElementSource(audio);
    this.bgmSource.connect(this.bgmGain!);

    // Apply fade-in
    if (fadeIn > 0 && this.bgmGain) {
      const now = ctx.currentTime;
      this.bgmGain.gain.setValueAtTime(0, now);
      this.bgmGain.gain.linearRampToValueAtTime(volume ?? this.bgmVolume, now + fadeIn / 1000);
    } else if (this.bgmGain && volume !== undefined) {
      this.bgmGain.gain.value = volume;
    }

    this.bgmElement = audio;
    this.currentBgmId = id;

    try {
      await audio.play();
      console.info(`[audio] BGM started: ${id}`);
    } catch (e) {
      console.warn(`[audio] BGM play failed (autoplay?): ${id}`, e);
      this.bgmElement = null;
      this.currentBgmId = null;
    }
  }

  /**
   * Stop current BGM with optional fade-out.
   */
  async stopBgm(fadeMs: number = 200): Promise<void> {
    if (!this.bgmElement || !this.ctx || !this.bgmGain) return;

    const audio = this.bgmElement;
    const gain = this.bgmGain;

    if (fadeMs > 0) {
      const now = this.ctx.currentTime;
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);

      await new Promise(resolve => setTimeout(resolve, fadeMs + 50));
    }

    audio.pause();
    audio.src = '';
    audio.load();
    this.bgmSource?.disconnect();
    this.bgmSource = null;
    this.bgmElement = null;
    this.currentBgmId = null;

    // Reset gain
    if (gain) gain.gain.value = this.bgmVolume;

    console.info('[audio] BGM stopped');
  }

  /**
   * Crossfade from current BGM to another.
   */
  async crossfade(toId: BgmId, duration: number = 1500): Promise<void> {
    if (!this.initialized || !this.ctx || !this.bgmGain) {
      await this.playBgm(toId);
      return;
    }

    const ctx = this.ctx;
    const now = ctx.currentTime;

    // Fade out current
    if (this.bgmElement && this.currentBgmId) {
      const oldAudio = this.bgmElement;
      const oldSource = this.bgmSource;
      
      // Create a temporary gain for the old audio to fade it independently
      const oldGain = ctx.createGain();
      oldGain.gain.setValueAtTime(this.bgmVolume, now);
      oldGain.gain.linearRampToValueAtTime(0, now + duration / 1000);
      oldGain.connect(this.masterGain!);
      
      // Reconnect old source through oldGain
      oldSource?.disconnect();
      oldSource?.connect(oldGain);

      setTimeout(() => {
        oldAudio.pause();
        oldAudio.src = '';
        oldSource?.disconnect();
      }, duration + 100);
    }

    // Fade in new
    await this.playBgm(toId, { fadeIn: duration, volume: 0 });
    if (this.bgmGain) {
      const now2 = ctx.currentTime;
      this.bgmGain.gain.setValueAtTime(0, now2);
      this.bgmGain.gain.linearRampToValueAtTime(this.bgmVolume, now2 + duration / 1000);
    }
  }

  // ---- MIDI ----

  /**
   * Load and cache a MIDI file.
   */
  private async loadMidiBuffer(id: MidiId): Promise<ArrayBuffer | null> {
    if (this.midiBuffer && this.currentMidiId === id) {
      return this.midiBuffer;
    }

    const src = MIDI_FILES[id];
    try {
      const resp = await fetch(src);
      if (!resp.ok) {
        console.warn(`[audio] MIDI file not found: ${src}`);
        return null;
      }
      const buffer = await resp.arrayBuffer();
      this.midiBuffer = buffer;
      return buffer;
    } catch (e) {
      console.warn(`[audio] MIDI load failed: ${src}`, e);
      return null;
    }
  }

  /**
   * Play a MIDI track via Web Audio synthesis.
   */
  async playMidi(
    id: MidiId,
    options: {
      loop?: boolean;
      volume?: number;
      speed?: number;
    } = {}
  ): Promise<void> {
    const ctx = await this.ensureContext();
    if (!ctx || !this.midiGain) return;

    // Stop current MIDI
    this.stopMidi();

    const buffer = await this.loadMidiBuffer(id);
    if (!buffer) return;

    this.midiPlayback = playMidi(ctx, this.midiGain, buffer, {
      loop: options.loop ?? true,
      volume: options.volume ?? this.midiVolume,
      speed: options.speed ?? 1.0,
    });

    this.currentMidiId = id;
    console.info(`[audio] MIDI started: ${id}`);
  }

  /**
   * Stop current MIDI playback.
   */
  stopMidi(): void {
    if (this.midiPlayback) {
      this.midiPlayback.stop();
      this.midiPlayback = null;
    }
    this.currentMidiId = null;
    this.midiBuffer = null;
  }

  /**
   * V15 P1: 战斗 MIDI 动态切换 — HP 阈值 / 倒计时触发。
   * 快速淡出旧 MIDI → 淡入新 MIDI，避免突兀断连。
   * @param toId 目标 MIDI 标识
   * @param options.speed 播放速度倍率（climax 层 BPM +20% → 1.2）
   */
  async switchMidi(
    toId: MidiId,
    options: { speed?: number } = {}
  ): Promise<void> {
    if (this.currentMidiId === toId) return; // 相同 MIDI 不重复切换
    const ctx = await this.ensureContext();
    if (!ctx || !this.midiGain) return;

    // 淡出旧 MIDI（100ms）
    const now = ctx.currentTime;
    this.midiGain.gain.cancelScheduledValues(now);
    this.midiGain.gain.linearRampToValueAtTime(0, now + 0.1);

    await new Promise<void>(resolve => {
      setTimeout(() => {
        this.midiGain?.gain.cancelScheduledValues(ctx.currentTime);
        resolve();
      }, 120);
    });

    // 停止旧 MIDI
    this.stopMidi();

    // 加载并播放新 MIDI
    const buffer = await this.loadMidiBuffer(toId);
    if (!buffer) return;

    this.midiPlayback = playMidi(ctx, this.midiGain, buffer, {
      loop: true,
      volume: this.midiVolume,
      speed: options.speed ?? 1.0,
    });

    // 淡入新 MIDI（200ms）
    this.midiGain.gain.setValueAtTime(0, ctx.currentTime);
    this.midiGain.gain.linearRampToValueAtTime(this.midiVolume, ctx.currentTime + 0.2);

    this.currentMidiId = toId;
    console.info(`[audio] MIDI switched: ${toId} speed=${options.speed ?? 1.0}`);
  }

  // ---- SFX ----

  /**
   * Play a sound effect.
   */
  async playSfx(id: SfxId): Promise<void> {
    const ctx = await this.ensureContext();
    if (!ctx || !this.sfxGain) return;

    playSfx(ctx, this.sfxGain, id);
  }

  // ---- Volume Controls ----

  setMasterVolume(v: number): void {
    this.masterVolume = Math.max(0, Math.min(1, v));
    if (this.masterGain) {
      this.masterGain.gain.value = this.isMuted ? 0 : this.masterVolume;
    }
    this.saveVolumeSettings();
  }

  setBgmVolume(v: number): void {
    this.bgmVolume = Math.max(0, Math.min(1, v));
    if (this.bgmGain) {
      this.bgmGain.gain.value = this.bgmVolume;
    }
    this.saveVolumeSettings();
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, v));
    if (this.sfxGain) {
      this.sfxGain.gain.value = this.sfxVolume;
    }
    this.saveVolumeSettings();
  }

  setMidiVolume(v: number): void {
    this.midiVolume = Math.max(0, Math.min(1, v));
    if (this.midiGain) {
      this.midiGain.gain.value = this.midiVolume;
    }
    this.saveVolumeSettings();
  }

  mute(): void {
    this.isMuted = true;
    if (this.masterGain) {
      this.masterGain.gain.value = 0;
    }
  }

  unmute(): void {
    this.isMuted = false;
    if (this.masterGain) {
      this.masterGain.gain.value = this.masterVolume;
    }
  }

  toggleMute(): boolean {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.isMuted;
  }

  getMuted(): boolean {
    return this.isMuted;
  }

  getMasterVolume(): number { return this.masterVolume; }
  getBgmVolume(): number { return this.bgmVolume; }
  getSfxVolume(): number { return this.sfxVolume; }

  // ---- Screen Transition ----
  // Implements design/v15-bgm-audio/README.md §1.5 场景过渡模型

  /**
   * Handle BGM transition when navigating between screens.
   * Call this from main.ts navigate() before rendering the new screen.
   */
  async onScreenTransition(
    fromScreen: ScreenId,
    toScreen: ScreenId,
  ): Promise<void> {
    await this.ensureContext();

    // Editor → Battle: 硬切 + 0.2s 静默
    if (fromScreen === 'editor' && toScreen === 'battle') {
      await this.stopBgm(100);
      // midi-sting will be triggered separately by deploy-loading
      return;
    }

    // Battle → Result: 硬切 (0s) — design doc: "延迟=信息泄露"
    if (fromScreen === 'battle' && toScreen === 'result') {
      // Don't stop here — screen-4-battle handles victory/defeat bgm
      return;
    }

    // Result → Armory Hall: Fade out → 0.5s silence → Fade in
    if (toScreen === 'armory-hall') {
      await this.stopBgm(300);
      await new Promise(r => setTimeout(r, 500));
      await this.playBgm('armory-hall', { fadeIn: 800 });
      return;
    }

    // Entering editor
    if (toScreen === 'editor') {
      await this.stopBgm(200);
      await this.playMidi('armory-hall-simple', { loop: true });
      return;
    }

    // Entering armory-hall
    if (toScreen === 'armory-hall') {
      await this.playBgm('armory-hall', { fadeIn: 500 });
      return;
    }

    // Entering battle — handled by screen-4-battle itself
    if (toScreen === 'battle') {
      return;
    }

    // Entering forge / sharpen — workspace screens, same as editor
    if (toScreen === 'forge' || toScreen === 'sharpen') {
      await this.stopBgm(200);
      await this.playMidi('armory-hall-simple', { loop: true });
      return;
    }

    // Entering my-works — browsing page, calm ambient
    if (toScreen === 'my-works') {
      await this.stopBgm(200);
      await this.playMidi('defeat-minimal', { loop: true, volume: 0.3 });
      return;
    }

    // Entering levels/projects — use defeat-minimal (ambient, sparse)
    if (toScreen === 'levels' || toScreen === 'projects') {
      await this.stopBgm(200);
      await this.playMidi('defeat-minimal', { loop: true, volume: 0.3 });
      return;
    }

    // Default: stop MIDI, no BGM
    this.stopMidi();
  }

  /**
   * Call when battle ends — play victory/defeat BGM.
   * @param winner "team_a" (player) | "team_b" (AI) | "draw"
   */
  async onBattleEnd(winner: 'team_a' | 'team_b' | 'draw'): Promise<void> {
    // Battle BGM stops — hard cut
    await this.stopBgm(50);
    this.stopMidi();

    if (winner === 'team_a') {
      await this.playBgm('victory', { loop: false });
    } else {
      await this.playBgm('defeat', { loop: false });
    }
  }

  /**
   * Call when deploy button is clicked — play sting.
   */
  async onDeploySubmit(): Promise<void> {
    await this.ensureContext();
    await this.playMidi('sting', { loop: false, volume: 0.6 });
  }

  /**
   * Clean up all audio resources.
   */
  async destroy(): Promise<void> {
    await this.stopBgm(0);
    this.stopMidi();
    
    if (this.ctx && this.ctx.state !== 'closed') {
      await this.ctx.close();
    }
    
    this.ctx = null;
    this.masterGain = null;
    this.bgmGain = null;
    this.midiGain = null;
    this.sfxGain = null;
    this.initialized = false;
    this.initPromise = null;
    console.info('[audio] AudioEngine destroyed');
  }

  // ---- Persistence ----

  private loadVolumeSettings(): void {
    try {
      const raw = localStorage.getItem('battlecode-audio-settings');
      if (raw) {
        const s = JSON.parse(raw);
        this.masterVolume = s.master ?? DEFAULT_MASTER_VOLUME;
        this.bgmVolume = s.bgm ?? DEFAULT_BGM_VOLUME;
        this.sfxVolume = s.sfx ?? DEFAULT_SFX_VOLUME;
        this.midiVolume = s.midi ?? DEFAULT_MIDI_VOLUME;
        this.isMuted = s.muted ?? false;
      }
    } catch { /* ignore */ }
  }

  private saveVolumeSettings(): void {
    try {
      localStorage.setItem('battlecode-audio-settings', JSON.stringify({
        master: this.masterVolume,
        bgm: this.bgmVolume,
        sfx: this.sfxVolume,
        midi: this.midiVolume,
        muted: this.isMuted,
      }));
    } catch { /* ignore */ }
  }
}

// ---- Singleton ----
export const audioEngine = new AudioEngine();

// Auto-init on first user interaction
let autoInitDone = false;
export function setupAutoInit(): void {
  if (autoInitDone) return;
  autoInitDone = true;

  const handler = () => {
    audioEngine.init();
    document.removeEventListener('click', handler);
    document.removeEventListener('keydown', handler);
  };
  document.addEventListener('click', handler, { once: true });
  document.addEventListener('keydown', handler, { once: true });
}
